import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import { Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyApprovals = () => {
  const [ptwList, setPtwList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyApprovals();
  }, []);

  const fetchMyApprovals = async () => {
    try {
      // Use pending-approval endpoint instead of my-approvals
      const response = await axios.get(`${API}/ptw/pending-approval`, { headers: getAuthHeaders() });
      setPtwList(response.data);
    } catch (error) {
      console.error('Error fetching my approvals:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'waiting_pic': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Waiting PIC' },
      'waiting_area_manager': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Waiting Area Manager' },
      'waiting_she': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Waiting SHE' },
      'waiting_management': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Waiting Leadership Team' },
      'waiting_closing': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Waiting Closing' },
      'waiting_closing_approval': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Waiting Closing Approval' },
      'needs_revision': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Needs Revision' },
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      'closed': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
    };

    const statusInfo = statusMap[status] || { bg: 'bg-slate-100', text: 'text-slate-800', label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredPTW = ptwList.filter(ptw => 
    ptw.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ptw.deskripsi_pekerjaan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 lg:ml-64 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0c3c30] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 lg:ml-64 overflow-y-auto">
        <div className="p-6 lg:p-12">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#0c3c30] mb-2">My Approvals</h1>
            <p className="text-slate-600">PTW yang menunggu approval Anda</p>
          </div>

          {ptwList.length > 0 && (
            <div className="mb-6 bg-amber-50 border border-amber-200 p-4 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-amber-800">Anda memiliki {ptwList.length} PTW yang menunggu approval</p>
                <p className="text-xs text-amber-700 mt-1">Silakan review dan berikan keputusan Anda</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by ID or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0c3c30] w-full"
                />
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Deskripsi Pekerjaan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Requester</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPTW.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                        {searchTerm ? 'Tidak ada PTW yang sesuai dengan pencarian' : 'Tidak ada PTW yang menunggu approval Anda'}
                      </td>
                    </tr>
                  ) : (
                    filteredPTW.map((ptw) => (
                      <tr key={ptw.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-900 font-mono">{ptw.id.substring(0, 8)}</td>
                        <td className="px-6 py-4 text-sm text-slate-900">{ptw.deskripsi_pekerjaan}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{ptw.requester_name}</td>
                        <td className="px-6 py-4">{getStatusBadge(ptw.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{formatDate(ptw.created_at)}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => navigate(`/ptw/${ptw.id}`)}
                            className="text-[#0c3c30] hover:text-[#082820] font-medium text-sm transition-colors"
                          >
                            Review & Approve
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MyApprovals;